/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useSelector } from 'react-redux';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useAppContext } from 'providers/AppProvider';
import CashSearchBox, { CashSearchBoxRef } from './CashSearchBox';
import { CashStarsIcon, TrashIcon } from 'assets/img/icons/icons';
import EmptyCashBack from './EmptyCashBack';
import CashBackList from './CashBackList';
import './cashSale.scss';
import CurrencySelector from './CurrencySelector';
import FeaturedProductsTable from './FeaturedProductsTable';
import DropDownMoreItems from './DropDownMoreItems';
import AmountInoutForSale from './AmountInoutForSale';
import Button from 'components/base/Button';
import { cashBackActions } from 'store/cash/cash.slice';

export const cashSaleBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'sale',
    active: true
  }
];

const CashSale = () => {
  const { t } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();
  const dispatch: Dispatch<any> = useDispatch();
  const { cashBack, selectedSalesValues: reduxSelectedSalesValues } =
    useSelector((state: any) => state.cashBack);

  const searchBoxRef = useRef<CashSearchBoxRef>(null);

  const [cashBackListData, setCashBackListData] = useState<any>([]);

  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [openFeaturedProducts, setOpenFeaturedProducts] = useState(false);
  const [selectedOptionMoreItem, setSelectedOptionMoreItem] = useState<
    string | null
  >(null);

  const [openDiscountAmount, setOpenDiscountAmount] = useState(false);
  const [openDiscountPercent, setOpenDiscountPercent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOption, setPendingOption] = useState<string | null>(null);

  const [selectedSalesValues, setSelectedSalesValues] = useState<
    { name: any; value: any }[]
  >([]);

  useEffect(() => {
    // Sync cashBackListData
    if (cashBack && cashBack.length > 0) {
      setCashBackListData((prev: any) => {
        const existingIds = new Set(prev.map((item: any) => item.id));
        const newItems = cashBack.filter(
          (item: any) => !existingIds.has(item.id)
        );
        return [...prev, ...newItems];
      });
    }
    // Sync selectedSalesValues
    if (reduxSelectedSalesValues && reduxSelectedSalesValues.length > 0) {
      setSelectedSalesValues(prev => {
        const existingNames = new Set(prev.map(item => item.name));
        const newItems = reduxSelectedSalesValues.filter(
          (item: any) => !existingNames.has(item.name)
        );
        return [...prev, ...newItems];
      });
    }
  }, [cashBack, reduxSelectedSalesValues]);

  const handleEmptyCashBackClick = () => {
    searchBoxRef.current?.focusAndOpen();
  };

  const handleCurrencyChange = (currency: any): void => {
    setSelectedCurrency(currency);
  };

  const handleConfirmModal = (confirm: boolean) => {
    if (confirm && pendingOption) {
      if (
        pendingOption === 'discount-amount' ||
        pendingOption === 'discount-percent'
      ) {
        // Clear existing discounts and add the new discount upon confirmation
        setSelectedSalesValues(prev => {
          const newValues = prev.filter(
            item =>
              item.name !== 'discount-amount' &&
              item.name !== 'discount-percent'
          );
          newValues.push({ name: pendingOption, value: null }); // Add new discount placeholder
          return newValues;
        });
        if (pendingOption === 'discount-amount') {
          setOpenDiscountAmount(true);
          setOpenDiscountPercent(false);
        } else if (pendingOption === 'discount-percent') {
          setOpenDiscountAmount(false);
          setOpenDiscountPercent(true);
        }
      } else if (pendingOption === 'wholesale-price') {
        // Add or update wholesalePrice without removing existing discounts
        setSelectedSalesValues(prev => {
          const existingWholesaleIndex = prev.findIndex(
            item => item.name === 'wholesale-price'
          );
          if (existingWholesaleIndex === 0) {
            return prev.map((item, index) =>
              index === 0 ? { ...item, value: true } : item
            );
          } else {
            // If wholesale-price is at another index or doesn't exist, remove discounts and add wholesale-price
            const newValues = prev.filter(
              item =>
                item.name !== 'discount-amount' &&
                item.name !== 'discount-percent'
            );
            if (existingWholesaleIndex === -1) {
              newValues.push({ name: 'wholesale-price', value: true });
            } else {
              newValues[existingWholesaleIndex] = {
                name: 'wholesale-price',
                value: true
              };
            }
            return newValues;
          }
        });
        setOpenDiscountAmount(false);
        setOpenDiscountPercent(false);
      }
      setSelectedOptionMoreItem(pendingOption);
      setPendingOption(null);
    } else {
      setSelectedSalesValues(prev => {
        const newValues = [...prev];
        newValues.pop(); // Remove the last item
        return newValues;
      });
      setSelectedOptionMoreItem(null);
      setPendingOption(null);
    }
    setShowConfirmModal(false);
  };

  const handleClearAll = () => {
    // Clear local state
    setCashBackListData([]);
    // Clear Redux state
    dispatch(cashBackActions.clearCashBackList());
    // Clear other states
    setSelectedSalesValues([]);
    setOpenDiscountAmount(false);
    setOpenDiscountPercent(false);
  };

  useEffect(() => {
    const hasDiscount =
      selectedSalesValues.some(
        item => item.name === 'discount-amount' && item.value > 0
      ) ||
      selectedSalesValues.some(
        item => item.name === 'discount-percent' && item.value > 0
      );

    // Check if switching between discount types or adding a new discount
    const hasDiscountAmount = selectedSalesValues.some(
      item => item.name === 'discount-amount' && item.value >= 0
    );
    const hasDiscountPercent = selectedSalesValues.some(
      item => item.name === 'discount-percent' && item.value >= 0
    );
    if (selectedOptionMoreItem && hasDiscount) {
      // Show confirmation modal when switching between discount types or adding a discount
      if (
        (selectedOptionMoreItem === 'discount-amount' && hasDiscountPercent) ||
        (selectedOptionMoreItem === 'discount-percent' && hasDiscountAmount)
      ) {
        setShowConfirmModal(true);
        setPendingOption(selectedOptionMoreItem);
      } else if (
        (selectedOptionMoreItem === 'discount-amount' && hasDiscountAmount) ||
        (selectedOptionMoreItem === 'discount-percent' && hasDiscountPercent)
      ) {
        // No action needed if selecting the same discount type again
      } else {
        setShowConfirmModal(true);
        setPendingOption(selectedOptionMoreItem);
      }
    } else if (selectedOptionMoreItem === 'discount-amount') {
      setOpenDiscountAmount(true);
      setOpenDiscountPercent(false);
    } else if (selectedOptionMoreItem === 'discount-percent') {
      setOpenDiscountAmount(false);
      setOpenDiscountPercent(true);
    } else if (selectedOptionMoreItem === 'wholesale-price') {
      setOpenDiscountAmount(false);
      setOpenDiscountPercent(false);
      setShowConfirmModal(false);
    } else {
      setOpenDiscountAmount(false);
      setOpenDiscountPercent(false);
    }
  }, [selectedOptionMoreItem, selectedSalesValues]);

  return (
    <div className="cash-main">
      <PageBreadcrumb items={cashSaleBreadcrumbItems} />
      <h2 className="mb-5">{t('sale')}</h2>
      <div
        className={
          theme === 'dark' ? 'cashmain-container-dark' : 'cashmain-container'
        }
      >
        <div className="main-search">
          <CashSearchBox
            cashBackListData={cashBackListData}
            setCashBackListData={setCashBackListData}
            ref={searchBoxRef}
          />
        </div>

        <div className="main-boxes">
          {cashBackListData?.length > 0 && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ position: 'fixed' }}>
                  {t('delete_all')}
                </Tooltip>
              }
            >
              <div className="boxes" onClick={handleClearAll}>
                <TrashIcon />
              </div>
            </OverlayTrigger>
          )}
          <div className="boxes">
            <DropDownMoreItems
              setSelectedOption={setSelectedOptionMoreItem}
              selectedSalesValues={selectedSalesValues}
              setSelectedSalesValues={setSelectedSalesValues}
            />
          </div>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                {t('featured_products')}
              </Tooltip>
            }
          >
            <div
              className="boxes"
              onClick={() => setOpenFeaturedProducts(true)}
            >
              <CashStarsIcon />
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                {t('exchange_rates')}
              </Tooltip>
            }
          >
            <div className="boxes">
              <CurrencySelector
                selectedCurrency={selectedCurrency}
                onCurrencyChange={handleCurrencyChange}
                disabled={true}
                className="custom-currency-selector"
              />
            </div>
          </OverlayTrigger>
        </div>
      </div>

      {cashBackListData.length === 0 ? (
        <EmptyCashBack onButtonClick={handleEmptyCashBackClick} />
      ) : (
        <CashBackList
          cashBackListData={cashBackListData}
          selectedSalesValues={selectedSalesValues}
          setSelectedSalesValues={setSelectedSalesValues}
          setCashBackListData={setCashBackListData}
          setSelectedOptionMoreItem={setSelectedOptionMoreItem}
        />
      )}

      <Modal
        show={openDiscountAmount}
        onHide={() => setOpenDiscountAmount(false)}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('Сумма скидки')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AmountInoutForSale
            selectedOption="discount-amount"
            setOpenDiscountAmount={setOpenDiscountAmount}
            setOpenDiscountPercent={setOpenDiscountPercent}
            setSelectedSalesValues={setSelectedSalesValues}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={openDiscountPercent}
        onHide={() => setOpenDiscountPercent(false)}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('Процентная скидка')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AmountInoutForSale
            selectedOption="discount-percent"
            setOpenDiscountAmount={setOpenDiscountAmount}
            setOpenDiscountPercent={setOpenDiscountPercent}
            setSelectedSalesValues={setSelectedSalesValues}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={openFeaturedProducts}
        onHide={() => setOpenFeaturedProducts(false)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('featured_products')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FeaturedProductsTable
            setCashBackListData={setCashBackListData}
            setOpenFeaturedProducts={setOpenFeaturedProducts}
            cashBackListData={cashBackListData}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => handleConfirmModal(false)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="confirm-modal-title">{t('attention')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('changing_discount_will_clear_previous')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleConfirmModal(false)}>
            {t('no')}
          </Button>
          <Button variant="primary" onClick={() => handleConfirmModal(true)}>
            {t('yes')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CashSale;
